/**
 * File: invoicePreviewService.ts
 * Path: apps/admin/src/shared/services/invoicePreviewService.ts
 * Description: The invoice's trips to the server and its saved copies.
 * previewInvoicePdf draws the PDF from the New invoice form as it stands
 * (nothing saved); saveInvoice gives it the next number, keeps it and returns
 * the same PDF. The admin has no PDF renderer of its own, so the preview and
 * the download are the same file. Saved invoices are read straight from
 * invoices/<id> (the server is the only writer).
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import {
  db,
  FunctionsService,
  INVOICE_COLLECTIONS,
  INVOICES_LIST_LIMIT,
  toSavedInvoice,
  type InvoiceDraft,
  type SavedInvoice,
} from "@idk/config";

export interface InvoicePdfResult {
  ready: boolean;
  reason?: string;
  filename?: string;
  base64?: string;
}

export interface SavedInvoiceResult {
  id: string;
  number: string;
  filename: string;
  base64: string;
}

export const invoicePreview = (invoice: InvoiceDraft) =>
  FunctionsService.call<{ invoice: InvoiceDraft }, InvoicePdfResult>("previewInvoicePdf", { invoice });

export const saveInvoice = (invoice: InvoiceDraft, contractId: string) =>
  FunctionsService.call<{ invoice: InvoiceDraft; contractId: string }, SavedInvoiceResult>("saveInvoice", {
    invoice,
    contractId,
  });

export const listInvoices = async (): Promise<SavedInvoice[]> => {
  const snap = await getDocs(
    query(collection(db, INVOICE_COLLECTIONS.invoices), orderBy("createdAt", "desc"), limit(INVOICES_LIST_LIMIT)),
  );
  return snap.docs.flatMap((d) => toSavedInvoice(d.id, d.data()) ?? []);
};

export const getInvoice = async (id: string): Promise<SavedInvoice | null> => {
  const snap = await getDoc(doc(db, INVOICE_COLLECTIONS.invoices, id));
  return snap.exists() ? toSavedInvoice(snap.id, snap.data()) : null;
};
